import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldIcon,
  GlobeIcon,
  PlusIcon,
  Edit3Icon,
  Trash2Icon,
  CheckCircleIcon,
  AlertTriangleIcon,
  SettingsIcon,
  NetworkIcon,
  RouterIcon,
  ServerIcon,
  ZapIcon,
  ActivityIcon,
  LayersIcon,
  CloudIcon,
  DatabaseIcon,
  MoveIcon,
  PlayIcon,
  SquareIcon,
  RefreshCwIcon,
  RotateCcwIcon,
  XIcon,
  Loader2Icon,
  RadioIcon,
} from 'lucide-react'
import { useState, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import ReactFlow, {
  type Node,
  type Edge,
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  Handle,
} from 'reactflow'
import { toast } from 'sonner'

import {
  ResponsiveActionRow,
  ResponsivePageHeader,
  ResponsiveTabs,
} from '@nebula/shared/components'
import { usePermissions } from '@nebula/shared/hooks'
import { useAuthStore } from '@nebula/shared/store'
import {
  networkPoolsApi,
  vpcApi,
  instancesApi,
  gatewayApi,
  endCustomerIpamApi,
  publicIpApi,
  getPublicIPStatusLabel,
  getPublicIPStatusColor,
} from '@nebula/shared/services'
import type { VPCListItem } from '@nebula/shared/services'
import { getErrorMessage } from '@nebula/shared/utils'
import { formatRecurringPrice, formatDate } from '@nebula/shared/utils/format'

import ChangeNetworkModal from '../components/ChangeNetworkModal'
import type { MoveDeVpcConcluido } from '../components/ChangeNetworkModal'
import CreateVPCModal from '../components/CreateVPCModal'
import DeleteVPCModal from '../components/DeleteVPCModal'
import type { ExclusaoDeVpcConcluida } from '../components/DeleteVPCModal'
import CreateGatewayModal from '../components/Gateway/CreateGatewayModal'
import IPAllocationsTable from '../components/IPAM/IPAllocationsTable'
import SubnetRangeConfig from '../components/IPAM/SubnetRangeConfig'
import MoveVpcWarningsBanner from '../components/MoveVpcWarningsBanner'
import {
  clearTopologyPositions,
  positionTopologyNodes,
  readTopologyPositions,
  saveTopologyPosition,
} from './topology-layout'

import 'reactflow/dist/style.css'

// Custom Node Components for React Flow
const InternetNode = ({ data: _data }: { data: any }) => (
  <div className="min-w-[150px] rounded-lg border-2 border-blue-600 bg-blue-500 px-4 py-3 text-white shadow-lg">
    <Handle type="source" position={Position.Bottom} />
    <div className="flex items-center space-x-2">
      <GlobeIcon className="size-5" />
      <div>
        <div className="font-semibold">Internet</div>
        <div className="text-xs opacity-90">Gateway Público</div>
      </div>
    </div>
  </div>
)

const VPCNode = ({ data }: { data: any }) => (
  <div className="min-w-[180px] rounded-lg border-2 border-purple-600 bg-purple-500 px-4 py-3 text-white shadow-lg">
    <Handle type="target" position={Position.Top} />
    <Handle type="source" position={Position.Bottom} />
    <div className="flex items-center space-x-2">
      <LayersIcon className="size-5" />
      <div>
        <div className="font-semibold">{data.name}</div>
        <div className="text-xs opacity-90">SDN: {data.network}</div>
        <div className="text-xs opacity-90">{data.vmCount} VMs</div>
      </div>
    </div>
  </div>
)

const VMNode = ({ data }: { data: any }) => (
  <div
    className={`min-w-[140px] rounded-lg border-2 px-3 py-2 shadow-md ${
      data.status === 'running'
        ? 'border-green-300 bg-green-50 text-green-800'
        : 'border-gray-300 bg-gray-50 text-gray-600'
    }`}
  >
    <Handle type="target" position={Position.Top} />
    <div className="flex items-center space-x-2">
      <ServerIcon className="size-4" />
      <div>
        <div className="text-sm font-medium">{data.name}</div>
        <div className="text-xs">{data.ip}</div>
        <div className="text-xs opacity-75">{data.os}</div>
      </div>
    </div>
  </div>
)

const LoadBalancerNode = ({ data }: { data: any }) => (
  <div className="min-w-[150px] rounded-lg border-2 border-orange-300 bg-orange-50 px-3 py-2 text-orange-800 shadow-md">
    <Handle type="target" position={Position.Top} />
    <Handle type="source" position={Position.Bottom} />
    <div className="flex items-center space-x-2">
      <ZapIcon className="size-4" />
      <div>
        <div className="text-sm font-medium">{data.name}</div>
        <div className="text-xs">{data.algorithm}</div>
        <div className="text-xs opacity-75">{data.targets} targets</div>
      </div>
    </div>
  </div>
)

const DatabaseNode = ({ data }: { data: any }) => (
  <div className="min-w-[140px] rounded-lg border-2 border-cyan-300 bg-cyan-50 px-3 py-2 text-cyan-800 shadow-md">
    <Handle type="target" position={Position.Top} />
    <div className="flex items-center space-x-2">
      <DatabaseIcon className="size-4" />
      <div>
        <div className="text-sm font-medium">{data.name}</div>
        <div className="text-xs">{data.ip}</div>
        <div className="text-xs opacity-75">{data.type}</div>
      </div>
    </div>
  </div>
)

const FirewallNode = ({ data }: { data: any }) => (
  <div className="min-w-[180px] rounded-lg border-2 border-red-600 bg-red-500 px-4 py-3 text-white shadow-lg">
    <Handle type="target" position={Position.Top} />
    <Handle type="source" position={Position.Bottom} />
    <div className="flex items-center space-x-2">
      <ShieldIcon className="size-5" />
      <div>
        <div className="font-semibold">{data.name}</div>
        <div className="text-xs opacity-90">Gateway: {data.gateway}</div>
        <div className="text-xs opacity-90">{data.rules} regras ativas</div>
      </div>
    </div>
  </div>
)

// Backend topology node components
const ClusterNode = ({ data }: { data: any }) => (
  <div className="min-w-[200px] rounded-lg border-2 border-indigo-600 bg-indigo-500 px-4 py-3 text-white shadow-lg">
    <Handle type="source" position={Position.Bottom} />
    <div className="flex items-center space-x-2">
      <CloudIcon className="size-6" />
      <div>
        <div className="text-lg font-semibold">{data.label}</div>
        {data.details && <div className="text-xs opacity-90">{data.details}</div>}
      </div>
    </div>
  </div>
)

const PoolNode = ({ data }: { data: any }) => (
  <div className="min-w-[180px] rounded-lg border-2 border-purple-600 bg-purple-500 px-4 py-3 text-white shadow-lg">
    <Handle type="target" position={Position.Top} />
    <Handle type="source" position={Position.Bottom} />
    <div className="flex items-center space-x-2">
      <NetworkIcon className="size-5" />
      <div>
        <div className="font-semibold">{data.label}</div>
        {data.details && <div className="text-xs opacity-90">{data.details}</div>}
        {data.metrics && (
          <div className="text-xs opacity-90">
            {data.metrics.used}/{data.metrics.total} ({data.metrics.percentage}%)
          </div>
        )}
      </div>
    </div>
  </div>
)

const SubnetNode = ({ data }: { data: any }) => (
  <div className="min-w-[160px] rounded-lg border-2 border-teal-600 bg-teal-500 px-3 py-2 text-white shadow-md">
    <Handle type="target" position={Position.Top} />
    <Handle type="source" position={Position.Bottom} />
    <div className="flex items-center space-x-2">
      <GlobeIcon className="size-4" />
      <div>
        <div className="text-sm font-medium">{data.label}</div>
        {data.details && <div className="text-xs opacity-90">{data.details}</div>}
      </div>
    </div>
  </div>
)

// Network node for bridges/VNETs
const NetworkNode = ({ data }: { data: any }) => {
  const isVPC = data.network_type === 'sdn_vnet'

  return (
    <div
      className={`min-w-[200px] rounded-lg border-2 px-4 py-3 shadow-lg ${
        isVPC
          ? 'border-teal-400 bg-gradient-to-br from-teal-500 to-cyan-600 text-white'
          : 'border-blue-600 bg-blue-500 text-white'
      }`}
    >
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
      <div className="flex items-center space-x-3">
        <div className={`rounded-lg p-2 ${isVPC ? 'bg-teal-400/30' : 'bg-blue-400/30'}`}>
          <NetworkIcon className="size-5" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">{data.name || data.label}</div>
          {isVPC ? (
            <>
              <div className="flex items-center space-x-1 text-xs opacity-90">
                <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-medium">
                  VPC
                </span>
                {data.cidr && <span className="opacity-75">{data.cidr}</span>}
              </div>
            </>
          ) : (
            <div className="text-xs opacity-90">Bridge de Rede</div>
          )}
          {data.vms_count !== undefined && data.vms_count > 0 && (
            <div className="mt-1 text-xs opacity-75">
              {data.vms_count} VM{data.vms_count > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Gateway node (Internet/Router)
const GatewayNode = ({ data }: { data: any }) => (
  <div className="min-w-[180px] rounded-lg border-2 border-indigo-600 bg-indigo-500 px-4 py-3 text-white shadow-lg">
    <Handle type="source" position={Position.Bottom} />
    <div className="flex items-center space-x-2">
      <RouterIcon className="size-5" />
      <div>
        <div className="font-semibold">{data.label}</div>
        {data.details && <div className="text-xs opacity-90">{data.details}</div>}
      </div>
    </div>
  </div>
)

const nodeTypes = {
  // Real topology nodes (from backend)
  gateway: GatewayNode,
  network: NetworkNode,
  vm: VMNode,
  // Legacy mock nodes (for backwards compatibility)
  internet: InternetNode,
  firewall: FirewallNode,
  vpc: VPCNode,
  loadbalancer: LoadBalancerNode,
  database: DatabaseNode,
  // Cloud Provider topology nodes
  cluster: ClusterNode,
  pool: PoolNode,
  subnet: SubnetNode,
}

// ===== My Public IPs Tab Component =====
const MyPublicIPsTab = () => {
  const { has } = usePermissions()
  const canReleaseIp = has('network:write')
  const {
    data: myIps,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['my-public-ips'],
    queryFn: () => publicIpApi.listMyIPs(),
  })

  const releaseMutation = useMutation({
    mutationFn: (ipId: string) => publicIpApi.releaseIP(ipId),
    onSuccess: () => {
      toast.success('IPv4 liberado.')
      refetch()
    },
    // Sem este ramo a recusa era ENGOLIDA (issue #291): o cliente clicava em
    // Confirmar, o backend respondia 409 dizendo o motivo e a tela não mudava
    // nada — a leitura de quem usa é "o botao nao faz nada". `getErrorMessage` e
    // nao `data.detail`: o handler global da API reembrulha o `detail` do
    // HTTPException em `{error:{message}}`, entao ler `detail` cai sempre no
    // texto generico e perde justamente a frase que explica a recusa.
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error))
    },
  })

  const [confirmRelease, setConfirmRelease] = useState<string | null>(null)

  const ips = myIps?.items || []

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-sm text-gray-500">Total</div>
          <div className="text-2xl font-bold text-gray-900">{ips.length}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-sm text-gray-500">Alocados</div>
          <div className="text-2xl font-bold text-blue-600">
            {ips.filter((ip) => ip.status === 'allocated').length}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-sm text-gray-500">Em Uso</div>
          <div className="text-2xl font-bold text-purple-600">
            {ips.filter((ip) => ip.status === 'attached').length}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-900">Meus IPs Públicos</h3>
          <a href="/marketplace" className="text-sm text-blue-600 hover:underline">
            Adquirir IPv4 →
          </a>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <Loader2Icon className="mx-auto mb-2 size-6 animate-spin" />
            Carregando...
          </div>
        ) : ips.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <GlobeIcon className="mx-auto mb-3 size-10 text-gray-300" />
            <p className="text-sm">Você ainda não possui IPs públicos.</p>
            <a
              href="/marketplace"
              className="mt-2 inline-block text-sm text-blue-600 hover:underline"
            >
              Adquirir IPv4 no Marketplace →
            </a>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-2 text-left">IP</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Cluster</th>
                {/* "Em uso por" e nao "VM": o endereco tambem pode estar na WAN de um
                    Cloud Gateway, e era esse o IP que aparecia com "-" nesta coluna
                    enquanto o botao Liberar era recusado (issue #291). */}
                <th className="px-4 py-2 text-left">Em uso por</th>
                <th className="px-4 py-2 text-left">Preço</th>
                <th className="px-4 py-2 text-left">Próxima cobrança</th>
                <th className="px-4 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ips.map((ip) => (
                <tr key={ip.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono font-medium text-gray-900">{ip.ip_address}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getPublicIPStatusColor(ip.status)}`}
                    >
                      {getPublicIPStatusLabel(ip.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{ip.cluster_name || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {/* O ID vem sempre que o IP esta preso a um gateway; o NOME so quando
                        ele resolve dentro da organizacao (fail-closed de tenancy). Sem nome,
                        a coluna ainda diz que o endereco esta em uso — voltar a "-" seria
                        reintroduzir o sintoma da #291. */}
                    {ip.vm_instance_name ||
                      (ip.cloud_gateway_id || ip.cloud_gateway_name
                        ? `Cloud Gateway${ip.cloud_gateway_name ? ` ${ip.cloud_gateway_name}` : ''}`
                        : '-')}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatRecurringPrice(ip.unit_price, ip.billing_cycle) || '-'}
                  </td>
                  {/* Sem data = fora da renovacao. Nao dizer "-" e so: a tela anuncia
                      "/mes" ao lado, e o cliente precisa saber quando (ou se) renova. */}
                  <td className="px-4 py-3 text-gray-600">
                    {ip.next_billing_date ? (
                      formatDate(ip.next_billing_date, false)
                    ) : (
                      <span className="text-gray-400">Sem renovação</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canReleaseIp &&
                      (ip.status === 'allocated' || ip.status === 'attached') &&
                      (confirmRelease === ip.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              releaseMutation.mutate(ip.id)
                              setConfirmRelease(null)
                            }}
                            className="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700"
                            disabled={releaseMutation.isPending}
                          >
                            Confirmar
                          </button>
                          <button
                            onClick={() => setConfirmRelease(null)}
                            className="rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-300"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmRelease(ip.id)}
                          className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                        >
                          Liberar
                        </button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

const VALID_TABS = ['topology', 'ipam', 'my-ips'] as const
type NetworkTabId = (typeof VALID_TABS)[number]

const NetworkingPage = () => {
  const queryClient = useQueryClient()
  const { has } = usePermissions()
  const canManageVpc = has('gateway:write')
  const userId = useAuthStore((state) => state.user?.id)
  const organizationId = useAuthStore((state) => state.activeOrganizationId)

  // URL-based tab navigation (?tab=topology)
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab: NetworkTabId = VALID_TABS.includes(searchParams.get('tab') as NetworkTabId)
    ? (searchParams.get('tab') as NetworkTabId)
    : 'topology'
  const setActiveTab = useCallback(
    (tab: NetworkTabId) => {
      setSearchParams({ tab }, { replace: true })
    },
    [setSearchParams],
  )
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 768,
  )

  const networkingTabs = [
    { id: 'topology' as const, label: 'Topologia', icon: LayersIcon },
    { id: 'ipam' as const, label: 'IPs', icon: DatabaseIcon },
    { id: 'my-ips' as const, label: 'IPs Públicos', icon: GlobeIcon },
  ]

  // VPC state
  const [showCreateVPCModal, setShowCreateVPCModal] = useState(false)
  const [vpcToDelete, setVpcToDelete] = useState<VPCListItem | null>(null)
  const [editingVpc, setEditingVpc] = useState<VPCListItem | null>(null)
  const [editVpcCidr, setEditVpcCidr] = useState('')
  const [editVpcWarnings, setEditVpcWarnings] = useState<string[]>([])
  // Avisos que a exclusão devolveu (VPC destino sem gateway, VM que trocou de
  // endereço fixo, DHCP não reconfigurado). O modal fecha; o aviso fica.
  const [avisosDaExclusao, setAvisosDaExclusao] = useState<ExclusaoDeVpcConcluida | null>(null)

  // Gateway state
  const [showCreateGatewayModal, setShowCreateGatewayModal] = useState(false)

  // Context menu state for VM network change
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean
    x: number
    y: number
    vmId: string | null
    vmName: string | null
    vmStatus: string | null
    vmClusterId: string | null
  }>({ visible: false, x: 0, y: 0, vmId: null, vmName: null, vmStatus: null, vmClusterId: null })

  // VM escolhida para o modal "Mover VM para VPC" (null = modal fechado).
  const [selectedVmForNetworkChange, setSelectedVmForNetworkChange] = useState<{
    id: string
    name: string
    clusterId: string | null
  } | null>(null)

  // Avisos que o move devolveu (VPC sem gateway, troca pendente no Proxmox). O
  // modal fecha assim que a VM é movida; o aviso fica aqui, ao lado da topologia
  // já atualizada, até o cliente dispensar.
  const [avisosDoMove, setAvisosDoMove] = useState<MoveDeVpcConcluido | null>(null)

  // Trocar de aba dispensa a faixa: ela vive dentro da aba de topologia, e sem
  // isto voltar de "IPs" a remontaria — rolando a página e tomando o foco de
  // quem só queria trocar de aba.
  useEffect(() => {
    setAvisosDoMove(null)
    setAvisosDaExclusao(null)
  }, [activeTab])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const syncMobileState = () => setIsMobile(mediaQuery.matches)
    syncMobileState()
    mediaQuery.addEventListener('change', syncMobileState)
    return () => mediaQuery.removeEventListener('change', syncMobileState)
  }, [])

  // Fetch customer network topology (VMs + networks)
  const {
    data: topologyData,
    isLoading: isLoadingTopology,
    refetch: refetchTopology,
  } = useQuery({
    queryKey: ['end-customer-topology', organizationId],
    queryFn: () => networkPoolsApi.getCustomerTopology(),
    enabled: !!organizationId,
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // Auto-refresh every minute
  })

  // Fetch customer VPCs
  const {
    data: vpcsData,
    isLoading: isLoadingVpcs,
    refetch: refetchVpcs,
  } = useQuery({
    queryKey: ['customer-vpcs'],
    queryFn: () => vpcApi.listCustomerVPCs(),
    staleTime: 30_000,
  })

  // Fetch customer gateway
  const { data: gateway } = useQuery({
    queryKey: ['gateway'],
    queryFn: gatewayApi.getMyGateway,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (gateway doesn't exist)
      if (error?.response?.status === 404) return false
      return failureCount < 3
    },
    // Auto-refresh during deploy (5s) or when running (30s)
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'provisioning' || status === 'configuring') return 5000
      if (status === 'running') return 30_000
      return false
    },
  })

  // IPAM queries
  const { data: ipamSubnetsData, isLoading: isLoadingIpamSubnets } = useQuery({
    queryKey: ['ec-ipam-subnets'],
    queryFn: endCustomerIpamApi.listSubnets,
    enabled: activeTab === 'ipam',
  })

  const ipamSubnet = ipamSubnetsData?.subnets?.[0] || null

  const { data: ipamAvailability } = useQuery({
    queryKey: ['ec-ipam-availability', ipamSubnet?.id],
    queryFn: () => endCustomerIpamApi.getAvailability(ipamSubnet!.id),
    enabled: activeTab === 'ipam' && !!ipamSubnet?.id,
  })

  const applyDhcpMutation = useMutation({
    mutationFn: (subnetId: string) => endCustomerIpamApi.applyDHCP(subnetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ec-ipam-subnets'] })
    },
  })

  // Update VPC CIDR mutation
  const updateVpcCidrMutation = useMutation({
    mutationFn: ({ vpcId, newCidr }: { vpcId: string; newCidr: string }) =>
      vpcApi.updateVPCCidr(vpcId, newCidr),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer-vpcs'] })
      queryClient.invalidateQueries({ queryKey: ['end-customer-topology'] })
      queryClient.invalidateQueries({ queryKey: ['ec-ipam-subnets'] })
      if (data.warnings && data.warnings.length > 0) {
        setEditVpcWarnings(data.warnings)
      } else {
        setEditingVpc(null)
        setEditVpcWarnings([])
      }
    },
  })

  // VM action mutation (start, stop, restart)
  const vmActionMutation = useMutation({
    mutationFn: ({
      instanceId,
      action,
    }: {
      instanceId: string
      action: 'start' | 'stop' | 'restart'
    }) => instancesApi.performAction(instanceId, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['end-customer-topology'] })
      handleCloseContextMenu()
    },
    onError: (error: any) => {
      console.error('VM action failed:', error)
    },
  })

  // Context menu handlers
  const handleNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
    // Only show context menu for VM nodes
    if (node.type !== 'vm') return

    event.preventDefault()

    // Extract VM UUID from node ID (format: "vm-UUID")
    const vmId = node.id.startsWith('vm-') ? node.id.slice(3) : node.id

    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      vmId,
      vmName: node.data?.name || node.data?.label || 'VM',
      vmStatus: node.data?.status || 'stopped',
      // Cluster da VM: o modal usa para esconder VPCs que o backend recusaria.
      vmClusterId: node.data?.cluster_id ?? null,
    })
  }, [])

  // Close context menu when clicking elsewhere
  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      vmId: null,
      vmName: null,
      vmStatus: null,
      vmClusterId: null,
    })
  }, [])

  // Handle opening change network modal from context menu
  const handleOpenChangeNetworkModal = useCallback(() => {
    if (contextMenu.vmId && contextMenu.vmName) {
      setSelectedVmForNetworkChange({
        id: contextMenu.vmId,
        name: contextMenu.vmName,
        clusterId: contextMenu.vmClusterId,
      })
      handleCloseContextMenu()
    }
  }, [contextMenu, handleCloseContextMenu])

  // Handle VM action (start, stop, restart)
  const handleVmAction = useCallback(
    (action: 'start' | 'stop' | 'restart') => {
      if (!contextMenu.vmId) return

      vmActionMutation.mutate({
        instanceId: contextMenu.vmId,
        action,
      })
    },
    [contextMenu.vmId, vmActionMutation],
  )

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        handleCloseContextMenu()
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [contextMenu.visible, handleCloseContextMenu])

  // React Flow state for topology - starts empty, populated from backend data
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (!userId || !organizationId || node.type === 'gateway') return
      saveTopologyPosition({ userId, organizationId }, node.id, node.position)
    },
    [userId, organizationId],
  )

  const restoreDefaultLayout = useCallback(() => {
    if (!userId || !organizationId || topologyData?.customer_id !== organizationId) return
    clearTopologyPositions({ userId, organizationId })
    setNodes((current) =>
      current.map((node) => ({
        ...node,
        position:
          topologyData.nodes.find((source) => source.id === node.id)?.position ?? node.position,
      })),
    )
  }, [userId, organizationId, topologyData, setNodes])

  // Update nodes and edges when topology data is loaded from backend
  useEffect(() => {
    if (
      topologyData &&
      topologyData.customer_id === organizationId &&
      userId &&
      topologyData.nodes &&
      topologyData.edges
    ) {
      const savedPositions = readTopologyPositions({ userId, organizationId })
      // Convert backend topology nodes to ReactFlow format
      const convertedNodes: Node[] = topologyData.nodes.map((node: any) => {
        // Determine node styling based on type
        const position = node.position || { x: 0, y: 0 }

        return {
          id: node.id,
          type: node.type, // gateway, network, vm
          position,
          data: {
            ...node.data,
            label: node.label || node.data?.label,
            name: node.label || node.data?.name,
            status: node.data?.status,
            ip: node.data?.ip,
            os: node.data?.os,
            network_type: node.data?.network_type,
            vms_count: node.data?.vms_count,
          },
          draggable: node.type !== 'gateway', // Gateway is fixed at top
        }
      })

      // Convert backend topology edges to ReactFlow format with proper styling
      const convertedEdges: Edge[] = topologyData.edges.map((edge: any) => {
        // Determine edge styling based on connection types
        let strokeColor = '#8b5cf6' // Default purple
        let strokeWidth = 2
        let strokeDasharray: string | undefined = undefined

        // Customize edge appearance
        if (edge.source === 'gateway' || edge.target === 'gateway') {
          strokeColor = '#6366f1' // Indigo for gateway connections
          strokeWidth = 3
        } else if (edge.type === 'network') {
          strokeColor = '#3b82f6' // Blue for network connections
        }

        // Dashed line for stopped VMs
        if (edge.label?.includes('stopped')) {
          strokeDasharray = '5,5'
          strokeWidth = 1
        }

        return {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: edge.type || 'smoothstep',
          animated: edge.animated || false,
          label: edge.label,
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { stroke: strokeColor, strokeWidth, strokeDasharray },
        }
      })

      setNodes(positionTopologyNodes(convertedNodes, savedPositions))
      setEdges(convertedEdges)
    } else {
      setNodes([])
      setEdges([])
    }
  }, [topologyData, organizationId, userId, setNodes, setEdges])

  const onConnect = useCallback(
    (params: any) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  )

  const renderTopologyTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <ResponsiveActionRow>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Topologia de Rede</h3>
          <p className="text-sm text-gray-600">
            Visualize a arquitetura da sua infraestrutura de rede
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:gap-2">
          {canManageVpc && (
            <button
              onClick={() => setShowCreateVPCModal(true)}
              className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50 sm:w-auto"
            >
              <PlusIcon className="size-4" />
              <span>Novo VPC</span>
            </button>
          )}
          <button
            onClick={() => refetchTopology()}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 sm:w-auto"
          >
            <ActivityIcon className="size-4" />
            <span>Atualizar</span>
          </button>
        </div>
      </ResponsiveActionRow>

      {/* Loading state for topology data */}
      {isLoadingTopology && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-6 text-center">
          <div className="mx-auto mb-3 size-8 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-blue-800">Carregando topologia da rede...</p>
        </div>
      )}

      {/* Info box when using real backend data */}
      {topologyData && !isLoadingTopology && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="size-5 shrink-0 text-green-600" />
            <div>
              <p className="text-sm text-green-800">
                <strong>Topologia Real:</strong> Visualizando infraestrutura de{' '}
                {topologyData.customer_name} ({topologyData.stats?.total_vms || 0} VMs,{' '}
                {topologyData.stats?.total_networks || 0} redes)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Empty state when no topology data */}
      {!isLoadingTopology &&
        (!topologyData || !topologyData.nodes || topologyData.nodes.length === 0) && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
            <AlertTriangleIcon className="mx-auto mb-3 size-8 text-amber-500" />
            <h4 className="mb-2 font-semibold text-amber-900">Nenhuma VM encontrada</h4>
            <p className="text-sm text-amber-700">
              Você ainda não possui VMs provisionadas. Acesse o Marketplace para contratar seus
              primeiros recursos.
            </p>
          </div>
        )}

      {/* VPCs Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <LayersIcon className="size-5 text-purple-600" />
            Suas VPCs
          </h4>
          {isLoadingVpcs && (
            <div className="size-4 animate-spin rounded-full border-b-2 border-purple-600" />
          )}
        </div>

        {/* VPCs Grid */}
        {vpcsData && vpcsData.vpcs && vpcsData.vpcs.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {vpcsData.vpcs.map((vpc) => (
              <div
                key={vpc.id}
                className={`rounded-lg border-2 bg-white p-4 shadow-sm transition-shadow hover:shadow-md ${
                  vpc.is_default ? 'border-purple-300' : 'border-gray-200'
                }`}
              >
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex items-center space-x-2">
                    <div
                      className={`rounded-lg p-2 ${vpc.is_default ? 'bg-purple-100' : 'bg-gray-100'}`}
                    >
                      <LayersIcon
                        className={`size-5 ${vpc.is_default ? 'text-purple-600' : 'text-gray-600'}`}
                      />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">{vpc.name}</h5>
                      <p className="text-xs text-gray-500">{vpc.vnet_id}</p>
                    </div>
                  </div>
                  {vpc.is_default && (
                    <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                      Padrão
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">CIDR:</span>
                    <span className="font-mono text-gray-900">{vpc.cidr}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Gateway:</span>
                    <span className="font-mono text-gray-900">{vpc.gateway}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">VNI:</span>
                    <span className="font-mono text-gray-900">{vpc.vni}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Alocações:</span>
                    <span className="text-gray-900">{vpc.current_allocations} IPs em uso</span>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                      vpc.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {vpc.enabled ? 'Ativa' : 'Inativa'}
                  </span>
                  {canManageVpc && (
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => {
                          setEditingVpc(vpc)
                          setEditVpcCidr(vpc.cidr)
                          setEditVpcWarnings([])
                        }}
                        className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-800"
                      >
                        <Edit3Icon className="size-4" />
                        <span>Editar CIDR</span>
                      </button>
                      <button
                        onClick={() => setVpcToDelete(vpc)}
                        className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800"
                      >
                        <Trash2Icon className="size-4" />
                        <span>Remover</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : isLoadingVpcs ? null : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
            <LayersIcon className="mx-auto mb-3 size-8 text-gray-400" />
            <h5 className="mb-2 font-medium text-gray-700">Nenhuma VPC encontrada</h5>
            <p className="mb-2 text-sm text-gray-500">
              Uma VPC é necessária para criar máquinas virtuais em rede privada.
            </p>
            <p className="mb-4 text-xs text-gray-400">
              Após criar a VPC, você poderá opcionalmente configurar um Cloud Gateway.
            </p>
            {canManageVpc && (
              <button
                onClick={() => setShowCreateVPCModal(true)}
                className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
              >
                <PlusIcon className="mr-2 size-4" />
                Criar VPC
              </button>
            )}
          </div>
        )}
      </div>

      {/* Network Info Cards - Using real stats from API */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-green-100 p-2">
              <ServerIcon className="size-5 text-green-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">VMs Totais</div>
              <div className="text-lg font-semibold">
                {topologyData?.stats?.running_vms || 0}/{topologyData?.stats?.total_vms || 0}
              </div>
              <div className="text-xs text-gray-400">
                {topologyData?.stats?.stopped_vms || 0} parada(s)
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-purple-100 p-2">
              <LayersIcon className="size-5 text-purple-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Redes SDN</div>
              <div className="text-lg font-semibold">{topologyData?.stats?.sdn_networks || 0}</div>
              <div className="text-xs text-gray-400">VNets ativas</div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <NetworkIcon className="size-5 text-blue-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Bridges</div>
              <div className="text-lg font-semibold">
                {topologyData?.stats?.bridge_networks || 0}
              </div>
              <div className="text-xs text-gray-400">Linux bridges</div>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-indigo-100 p-2">
              <GlobeIcon className="size-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Total Redes</div>
              <div className="text-lg font-semibold">
                {topologyData?.stats?.total_networks || 0}
              </div>
              <div className="text-xs text-gray-400">Conexões</div>
            </div>
          </div>
        </div>
      </div>

      {/* A faixa mora aqui, e não no topo da página: o move só é acionável pelo
          menu de contexto de um nó do canvas, ~1000px abaixo do header. Lá em
          cima o aviso nasceria fora da viewport de quem acabou de clicar. */}
      {avisosDoMove && (
        <MoveVpcWarningsBanner
          sujeito={avisosDoMove.vmName}
          vpcName={avisosDoMove.vpcName}
          warnings={avisosDoMove.warnings}
          onDismiss={() => setAvisosDoMove(null)}
        />
      )}

      {/* Exclusão de VPC que migrou recursos: o modal fecha, a consequência fica
          (endereço fixo trocado, destino sem gateway, DHCP não reconfigurado). */}
      {avisosDaExclusao && (
        <MoveVpcWarningsBanner
          sujeito={avisosDaExclusao.sujeito}
          vpcName={avisosDaExclusao.vpcName}
          warnings={avisosDaExclusao.warnings}
          onDismiss={() => setAvisosDaExclusao(null)}
        />
      )}

      {/* Topology Visualization */}
      {isMobile ? (
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-800">
              Visualização completa da topologia indisponível em telas pequenas. Use os cards abaixo
              para operação mobile.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-sm text-gray-500">Redes detectadas</p>
              <p className="text-xl font-semibold text-gray-900">
                {topologyData?.stats?.total_networks || 0}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-sm text-gray-500">VMs ativas</p>
              <p className="text-xl font-semibold text-gray-900">
                {topologyData?.stats?.running_vms || 0}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <h4 className="font-semibold text-gray-900">Diagrama da Rede</h4>
                {topologyData?.customer_id === organizationId && (
                  <button
                    onClick={restoreDefaultLayout}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  >
                    <RotateCcwIcon className="size-3.5" />
                    Restaurar layout padrão
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <div className="size-3 rounded bg-indigo-500"></div>
                  <span>Gateway</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="size-3 rounded bg-teal-500"></div>
                  <span>VPC</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="size-3 rounded bg-blue-500"></div>
                  <span>Bridge</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="size-3 rounded bg-green-500"></div>
                  <span>VM Ativa</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="size-3 rounded bg-gray-400"></div>
                  <span>VM Parada</span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ height: '600px' }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onNodeDragStop={handleNodeDragStop}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeContextMenu={handleNodeContextMenu}
              nodeTypes={nodeTypes}
              fitView
              attributionPosition="bottom-left"
            >
              <Background />
              <Controls />
              <MiniMap
                nodeStrokeColor={(n) => {
                  if (n.type === 'gateway') return '#6366f1' // Indigo
                  if (n.type === 'network')
                    return n.data?.network_type === 'sdn_vnet' ? '#14b8a6' : '#3b82f6' // Teal for VPC
                  if (n.type === 'vm') return n.data?.status === 'running' ? '#10b981' : '#6b7280'
                  // Legacy types
                  if (n.type === 'internet') return '#3b82f6'
                  if (n.type === 'firewall') return '#ef4444'
                  if (n.type === 'vpc') return '#14b8a6'
                  if (n.type === 'loadbalancer') return '#f97316'
                  if (n.type === 'database') return '#06b6d4'
                  return '#6b7280'
                }}
                nodeColor={(n) => {
                  if (n.type === 'gateway') return '#e0e7ff' // Indigo light
                  if (n.type === 'network')
                    return n.data?.network_type === 'sdn_vnet' ? '#ccfbf1' : '#dbeafe' // Teal light for VPC
                  if (n.type === 'vm') return n.data?.status === 'running' ? '#dcfce7' : '#f3f4f6'
                  // Legacy types
                  if (n.type === 'internet') return '#dbeafe'
                  if (n.type === 'firewall') return '#fecaca'
                  if (n.type === 'vpc') return '#ccfbf1'
                  if (n.type === 'loadbalancer') return '#fed7aa'
                  if (n.type === 'database') return '#cffafe'
                  return '#f3f4f6'
                }}
                maskColor="rgba(0, 0, 0, 0.1)"
              />
            </ReactFlow>
          </div>
        </div>
      )}

      {/* Network Details - Dynamic from topology data */}
      {topologyData && topologyData.nodes && topologyData.nodes.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h4 className="mb-4 font-semibold text-gray-900">Redes Conectadas</h4>
            <div className="space-y-3">
              {topologyData.nodes
                .filter((node: any) => node.type === 'network')
                .map((network: any) => {
                  const isVPC = network.data?.network_type === 'sdn_vnet'
                  return (
                    <div
                      key={network.id}
                      className={`flex items-center justify-between rounded-lg p-3 ${
                        isVPC ? 'bg-teal-50' : 'bg-blue-50'
                      }`}
                    >
                      <div>
                        <div className={`font-medium ${isVPC ? 'text-teal-900' : 'text-blue-900'}`}>
                          {network.data?.name || network.label || network.data?.label}
                        </div>
                        <div
                          className={`flex items-center space-x-2 text-sm ${
                            isVPC ? 'text-teal-600' : 'text-blue-600'
                          }`}
                        >
                          <span
                            className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                              isVPC ? 'bg-teal-200 text-teal-800' : 'bg-blue-200 text-blue-800'
                            }`}
                          >
                            {isVPC ? 'VPC' : 'Bridge'}
                          </span>
                          {network.data?.cidr && (
                            <span className="opacity-75">{network.data.cidr}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div
                          className={`text-sm font-medium ${
                            isVPC ? 'text-teal-700' : 'text-blue-700'
                          }`}
                        >
                          {network.data?.vms_count || 0} VM
                          {(network.data?.vms_count || 0) === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                  )
                })}
              {topologyData.nodes.filter((node: any) => node.type === 'network').length === 0 && (
                <p className="py-4 text-center text-sm text-gray-500">Nenhuma rede encontrada</p>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h4 className="mb-4 font-semibold text-gray-900">Máquinas Virtuais</h4>
            <div className="max-h-64 space-y-3 overflow-y-auto">
              {topologyData.nodes
                .filter((node: any) => node.type === 'vm')
                .map((vm: any) => (
                  <div
                    key={vm.id}
                    className={`flex items-center justify-between rounded-lg p-3 ${
                      vm.data?.status === 'running' ? 'bg-green-50' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <ServerIcon
                        className={`size-4 ${
                          vm.data?.status === 'running' ? 'text-green-600' : 'text-gray-400'
                        }`}
                      />
                      <div>
                        <div
                          className={`font-medium ${
                            vm.data?.status === 'running' ? 'text-green-900' : 'text-gray-700'
                          }`}
                        >
                          {vm.label || vm.data?.name}
                        </div>
                        <div
                          className={`text-sm ${
                            vm.data?.status === 'running' ? 'text-green-600' : 'text-gray-500'
                          }`}
                        >
                          {vm.data?.ip || 'IP não configurado'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          vm.data?.status === 'running'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {vm.data?.status === 'running' ? 'Ativa' : 'Parada'}
                      </span>
                    </div>
                  </div>
                ))}
              {topologyData.nodes.filter((node: any) => node.type === 'vm').length === 0 && (
                <p className="py-4 text-center text-sm text-gray-500">Nenhuma VM encontrada</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )

  const renderIPAMTab = () => {
    if (isLoadingIpamSubnets) {
      return (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2Icon className="mr-2 size-5 animate-spin" />
          Carregando subnets...
        </div>
      )
    }

    if (!ipamSubnet) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <DatabaseIcon className="mb-4 size-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhum Subnet Encontrado</h3>
          <p className="max-w-md text-sm text-gray-600">
            Sua organização ainda não possui subnets configurados. Crie uma VPC primeiro para que o
            subnet seja provisionado automaticamente.
          </p>
        </div>
      )
    }

    return (
      <div className="space-y-8">
        {/* Availability Summary */}
        {ipamAvailability && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {ipamAvailability.dhcp_range && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <div className="mb-2 flex items-center space-x-2">
                  <RadioIcon className="size-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">Faixa DHCP</span>
                </div>
                <p className="text-2xl font-bold text-green-700">
                  {ipamAvailability.dhcp_range.available}
                </p>
                <p className="text-xs text-green-600">
                  de {ipamAvailability.dhcp_range.total} disponíveis
                </p>
              </div>
            )}
            {ipamAvailability.static_range && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="mb-2 flex items-center space-x-2">
                  <ServerIcon className="size-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Faixa Estática</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">
                  {ipamAvailability.static_range.available}
                </p>
                <p className="text-xs text-blue-600">
                  de {ipamAvailability.static_range.total} disponíveis
                </p>
              </div>
            )}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-2 flex items-center space-x-2">
                <NetworkIcon className="size-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-800">Sem Range</span>
              </div>
              <p className="text-2xl font-bold text-gray-700">
                {ipamAvailability.unranged?.available ?? 0}
              </p>
              <p className="text-xs text-gray-600">
                de {ipamAvailability.unranged?.total ?? 0} total
              </p>
            </div>
          </div>
        )}

        {/* Range Configuration */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="mb-4 flex items-center text-base font-semibold text-gray-900">
            <SettingsIcon className="mr-2 size-5 text-gray-500" />
            Configuração de Ranges
          </h3>
          <SubnetRangeConfig subnet={ipamSubnet} />
        </div>

        {/* DHCP Apply Button */}
        {ipamSubnet.dhcp_range_start &&
          ipamSubnet.dhcp_range_end &&
          gateway &&
          gateway.status === 'running' && (
            <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4">
              <div>
                <h4 className="text-sm font-medium text-green-800">DHCP via Gateway</h4>
                <p className="text-xs text-green-600">
                  Enviar configuração DHCP para o gateway ({gateway.name}).
                </p>
              </div>
              <button
                onClick={() => applyDhcpMutation.mutate(ipamSubnet.id)}
                disabled={applyDhcpMutation.isPending}
                className="inline-flex items-center rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:opacity-50"
              >
                {applyDhcpMutation.isPending ? (
                  <Loader2Icon className="mr-1 size-4 animate-spin" />
                ) : (
                  <PlayIcon className="mr-1 size-4" />
                )}
                Aplicar DHCP
              </button>
            </div>
          )}

        {/* Allocations Table */}
        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="mb-4 text-base font-semibold text-gray-900">Alocações de IP</h3>
          <IPAllocationsTable subnetId={ipamSubnet.id} />
        </div>
      </div>
    )
  }

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Rede & Segurança"
        description="Configure firewall, load balancers, VPN e DNS para sua infraestrutura"
        icon={NetworkIcon}
      />

      {/* Tabs */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="px-4 pt-4 md:px-6 md:pt-6">
          <ResponsiveTabs
            tabs={networkingTabs}
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel="Abas de rede e segurança"
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'topology' && renderTopologyTab()}
          {activeTab === 'ipam' && renderIPAMTab()}
          {activeTab === 'my-ips' && <MyPublicIPsTab />}
        </div>
      </div>

      {/* Create VPC Modal */}
      <CreateVPCModal
        isOpen={showCreateVPCModal}
        onClose={() => setShowCreateVPCModal(false)}
        onCreated={() => refetchVpcs()}
      />

      {/* Excluir VPC: modal próprio porque excluir MOVE as VMs do cliente para
            outra rede — com endereço, cloud-init e DHCP junto (#804). */}
      <DeleteVPCModal
        vpc={vpcToDelete}
        vpcs={vpcsData?.vpcs ?? []}
        onClose={() => setVpcToDelete(null)}
        onDeleted={(resultado) => {
          // Sem aviso não há faixa; com aviso, a anterior é substituída — e a
          // do move sai junto, para não haver duas disputando rolagem e foco.
          setAvisosDoMove(null)
          setAvisosDaExclusao(resultado.warnings.length > 0 ? resultado : null)
        }}
      />

      {/* Edit VPC CIDR Modal */}
      {editingVpc && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
          <div className="mx-4 w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900">Editar CIDR da VPC</h3>
              <p className="mt-1 text-sm text-gray-500">Altere o range de IPs da rede virtual</p>
            </div>

            <div className="space-y-4 p-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">VPC</label>
                <p className="text-sm text-gray-900">{editingVpc.name}</p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">CIDR Atual</label>
                <p className="font-mono text-sm text-gray-500">{editingVpc.cidr}</p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Novo CIDR</label>
                <input
                  type="text"
                  value={editVpcCidr}
                  onChange={(e) => setEditVpcCidr(e.target.value)}
                  placeholder="ex: 10.0.0.0/24"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {editingVpc.current_allocations > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm text-amber-800">
                    <strong>{editingVpc.current_allocations}</strong> IP(s) alocado(s) devem caber
                    no novo range.
                  </p>
                </div>
              )}

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-600">
                  DHCP e faixas estáticas podem ser redefinidas se não couberem no novo CIDR.
                </p>
              </div>

              {updateVpcCidrMutation.isError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-800">
                    {(updateVpcCidrMutation.error as any)?.response?.data?.error?.message ||
                      (updateVpcCidrMutation.error as any)?.response?.data?.detail ||
                      (updateVpcCidrMutation.error as Error)?.message ||
                      'Erro ao atualizar CIDR'}
                  </p>
                </div>
              )}

              {editVpcWarnings.length > 0 && (
                <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-medium text-amber-800">CIDR atualizado com avisos:</p>
                  {editVpcWarnings.map((w, i) => (
                    <p key={i} className="text-sm text-amber-700">
                      • {w}
                    </p>
                  ))}
                  <button
                    onClick={() => {
                      setEditingVpc(null)
                      setEditVpcWarnings([])
                    }}
                    className="mt-2 text-sm font-medium text-purple-600 hover:text-purple-800"
                  >
                    Fechar
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 border-t border-gray-200 p-6">
              <button
                onClick={() => {
                  setEditingVpc(null)
                  setEditVpcWarnings([])
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  updateVpcCidrMutation.mutate({ vpcId: editingVpc.id, newCidr: editVpcCidr })
                }}
                disabled={
                  updateVpcCidrMutation.isPending || !editVpcCidr || editVpcCidr === editingVpc.cidr
                }
                className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {updateVpcCidrMutation.isPending ? (
                  <>
                    <div className="size-4 animate-spin rounded-full border-b-2 border-white" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <span>Salvar</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu for VM Nodes */}
      {contextMenu.visible && (
        <div
          className="fixed z-50 min-w-[200px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-gray-100 px-3 py-2">
            <div className="text-sm font-medium text-gray-900">{contextMenu.vmName}</div>
            <div
              className={`text-xs ${contextMenu.vmStatus === 'running' ? 'text-green-600' : 'text-gray-500'}`}
            >
              {contextMenu.vmStatus === 'running' ? 'Em execução' : 'Parada'}
            </div>
          </div>

          {/* VM Control Actions */}
          <div className="border-b border-gray-100">
            {contextMenu.vmStatus === 'running' ? (
              <>
                <button
                  onClick={() => handleVmAction('stop')}
                  disabled={vmActionMutation.isPending}
                  className="flex w-full items-center space-x-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                >
                  <SquareIcon className="size-4" />
                  <span>{vmActionMutation.isPending ? 'Parando...' : 'Parar VM'}</span>
                </button>
                <button
                  onClick={() => handleVmAction('restart')}
                  disabled={vmActionMutation.isPending}
                  className="flex w-full items-center space-x-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700 disabled:opacity-50"
                >
                  <RefreshCwIcon className="size-4" />
                  <span>{vmActionMutation.isPending ? 'Reiniciando...' : 'Reiniciar VM'}</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => handleVmAction('start')}
                disabled={vmActionMutation.isPending}
                className="flex w-full items-center space-x-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 disabled:opacity-50"
              >
                <PlayIcon className="size-4" />
                <span>{vmActionMutation.isPending ? 'Iniciando...' : 'Iniciar VM'}</span>
              </button>
            )}
          </div>

          {/* Network Actions */}
          <button
            onClick={handleOpenChangeNetworkModal}
            disabled={vmActionMutation.isPending}
            className="flex w-full items-center space-x-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 disabled:opacity-50"
          >
            <MoveIcon className="size-4" />
            <span>Mover para VPC</span>
          </button>

          {/* Close */}
          <div className="border-t border-gray-100">
            <button
              onClick={handleCloseContextMenu}
              className="flex w-full items-center space-x-2 px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
            >
              <XIcon className="size-4" />
              <span>Fechar</span>
            </button>
          </div>
        </div>
      )}

      {/* Change Network Modal */}
      {/* Invalida topologia e lista de VPCs por conta própria ao mover. */}
      <ChangeNetworkModal
        vm={selectedVmForNetworkChange}
        vpcs={vpcsData?.vpcs ?? []}
        onClose={() => setSelectedVmForNetworkChange(null)}
        onMoved={(resultado) => {
          // Sem aviso não há faixa; com aviso, a anterior é substituída (o texto
          // é da VPC de agora, não da do move passado). E a faixa da exclusão sai
          // junto: duas faixas montadas ao mesmo tempo fazem `scrollIntoView` +
          // `focus()` uma por cima da outra, e a segunda rouba o foco da primeira.
          setAvisosDaExclusao(null)
          setAvisosDoMove(resultado.warnings.length > 0 ? resultado : null)
        }}
      />

      {/* Create Gateway Modal */}
      {showCreateGatewayModal && (
        <CreateGatewayModal
          onClose={() => setShowCreateGatewayModal(false)}
          onCreated={() => {
            setShowCreateGatewayModal(false)
            queryClient.invalidateQueries({ queryKey: ['gateway'] })
            queryClient.invalidateQueries({ queryKey: ['end-customer-topology'] })
          }}
        />
      )}
    </div>
  )
}

export default NetworkingPage
